import type { CatalogFilters } from '@/data/catalog/search';
import { CatalogSort } from '@/data/catalog/search';
import { catalogUrlKeys } from '@/packages/courses/types/url-params';
import type {
  CatalogUrlKey,
  CatalogUrlParams,
} from '@/packages/courses/types/url-params';

const DEFAULT_PAGE = 1;
const DEFAULT_SORT = CatalogSort.Relevance;

export const getCatalogParamsFromUrl = (
  search: URLSearchParams
): CatalogUrlParams => {
  const params: CatalogUrlParams = {};

  for (const key of Object.values(catalogUrlKeys)) {
    const value = search.get(key);
    if (value == null || value === '') continue;

    if (key === 'page') {
      const num = parseInt(value, 10);
      if (!Number.isNaN(num) && num >= 1) params.page = num;
      continue;
    }

    if (key === 'sort') {
      params.sort = [value];
      continue;
    }

    params[key as Exclude<CatalogUrlKey, 'page'>] = value
      .split(',')
      .filter(Boolean);
  }

  return params;
};

export interface CatalogSearchRequestFromUrl {
  filters?: CatalogFilters;
  sort: CatalogSort;
  page: number;
  page_size: number;
}

export const urlParamsToCatalogRequest = (
  params: CatalogUrlParams
): CatalogSearchRequestFromUrl => {
  const filters: CatalogFilters = {};
  let sort = DEFAULT_SORT;
  let page = DEFAULT_PAGE;

  if (params.course_types?.length) {
    filters.course_types = params.course_types;
  }
  if (params.modalities?.length) {
    filters.modalities = params.modalities;
  }
  if (params.knowledge_areas?.length) {
    filters.knowledge_areas = params.knowledge_areas
      .map((id) => parseInt(id, 10))
      .filter((n) => !Number.isNaN(n));
  }
  if (params.institutions?.length) {
    filters.institutions = params.institutions
      .map((id) => parseInt(id, 10))
      .filter((n) => !Number.isNaN(n));
  }
  if (params.location?.length) {
    const [state, city] = params.location;
    if (state) filters.states = [state];
    if (city) filters.city = city;
  }
  if (params.price_min?.length || params.price_max?.length) {
    const min = params.price_min?.[0];
    const max = params.price_max?.[0];
    filters.budget = { min: 0, max: 0 };
    if (min != null) {
      const n = parseFloat(min);
      if (!Number.isNaN(n)) filters.budget!.min = n;
    }
    if (max != null) {
      const n = parseFloat(max);
      if (!Number.isNaN(n)) filters.budget!.max = n;
    }
    if (filters.budget!.min === 0 && filters.budget!.max === 0) {
      sort = CatalogSort.FreeOriginalPriceFirst;
    }
  }
  if (params.terms?.length) {
    filters.terms = params.terms.join(' ').trim();
  }
  if (params.free_courses?.length && params.free_courses[0] === 'true') {
    filters.budget = { min: 0, max: 0 };
    sort = CatalogSort.FreeOriginalPriceFirst;
  }
  if (
    params.unibrad_included?.length &&
    params.unibrad_included[0] === 'true'
  ) {
    filters.features = [...(filters.features ?? []), 'unibrad_included'];
  }

  if (params.sort?.[0]) {
    const s = params.sort[0];
    if (
      s === CatalogSort.PriceAsc ||
      s === CatalogSort.PriceDesc ||
      s === CatalogSort.Relevance ||
      s === CatalogSort.FreeOriginalPriceFirst
    ) {
      sort = s;
    }
  }

  if (params.page != null && params.page >= 1) {
    page = params.page;
  }

  const result: CatalogSearchRequestFromUrl = {
    sort,
    page,
    page_size: 50,
  };
  if (Object.keys(filters).length > 0) result.filters = filters;
  return result;
};

export const setCatalogUrlParam = (
  current: URLSearchParams,
  key: CatalogUrlKey,
  value: string | number | string[] | undefined
): URLSearchParams => {
  const next = new URLSearchParams(current);
  // any filter or sort change invalidates the current page
  if (key !== 'page') next.delete('page');

  if (
    value === undefined ||
    value === '' ||
    (Array.isArray(value) && value.length === 0)
  ) {
    next.delete(key);
    return next;
  }

  if (key === 'page') {
    const num = typeof value === 'number' ? value : parseInt(String(value), 10);
    if (Number.isNaN(num) || num < 1) {
      next.delete(key);
    } else {
      next.set(key, String(num));
    }
    return next;
  }

  const str = Array.isArray(value)
    ? value.filter(Boolean).join(',')
    : String(value);
  if (str) next.set(key, str);
  else next.delete(key);

  return next;
};

export const resetCatalogUrlParams = (
  current: URLSearchParams,
  keys: CatalogUrlKey[]
): URLSearchParams => {
  const next = new URLSearchParams(current);
  next.delete('page');
  for (const key of keys) {
    next.delete(key);
  }
  return next;
};
